import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Mail, User, Phone, PhoneOff, Pencil, X, Check, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VapiAgentCursor } from "@/components/ui/vapi-agent-cursor";
import { CollectedUserData } from "@/hooks/useVapiDataCollection";

// Custom TypedText component - clean animated typing with visible cursor
function TypedText({ text, delay = 0, speed = 40 }: { text: string; delay?: number; speed?: number }) {
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setDisplayedText("");
    setIsTyping(false);
    setIsComplete(false);
    
    const startTimeout = setTimeout(() => {
      setIsTyping(true);
      let currentIndex = 0;
      
      const interval = setInterval(() => {
        if (currentIndex < text.length) {
          setDisplayedText(text.slice(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(interval);
          setIsTyping(false);
          setIsComplete(true);
        }
      }, speed);
      
      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(startTimeout);
  }, [text, delay, speed]);

  return (
    <span className="text-white font-medium text-base inline-flex items-center">
      {displayedText}
      {/* Blinking cursor - visible during typing */}
      {(isTyping || !isComplete) && (
        <motion.span
          animate={{ opacity: [1, 0, 1] }}
          transition={{ duration: 0.6, repeat: Infinity }}
          className="inline-block w-0.5 h-5 bg-emerald-400 ml-0.5"
        />
      )}
    </span>
  );
}

interface DataConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: CollectedUserData;
  onConfirm: () => Promise<void>;
  onEndCall?: () => void;
  onDataChange?: (newData: CollectedUserData) => void;
  isSending?: boolean;
  sendError?: string | null;
}

export function DataConfirmationModal({
  open,
  onOpenChange,
  data,
  onConfirm,
  onEndCall,
  onDataChange,
  isSending = false,
  sendError = null,
}: DataConfirmationModalProps) {
  const navigate = useNavigate();
  
  // Success state for showing confirmation before redirect
  const [sendSuccess, setSendSuccess] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editField, setEditField] = useState<'name' | 'email' | 'phone' | 'all' | null>(null);
  const [editValues, setEditValues] = useState({
    username: data.username || '',
    email: data.email || '',
    phone: data.phone || '',
  });

  // Sync edit values when data changes
  useEffect(() => {
    setEditValues({
      username: data.username || '',
      email: data.email || '',
      phone: data.phone || '',
    });
  }, [data.username, data.email, data.phone]);

  // Cursor animation state - orchestrates move → click → write sequence
  const [cursorField, setCursorField] = useState<'name' | 'email' | 'phone' | null>(null);
  const [showCursor, setShowCursor] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [textToType, setTextToType] = useState("");

  // Get the text to display for each field
  const getFieldText = (field: 'name' | 'email' | 'phone' | null) => {
    switch (field) {
      case 'name': return data.username ? `"${data.username}"` : "Checking...";
      case 'email': return data.email ? `"${data.email}"` : "Verifying...";
      case 'phone': return data.phone ? `"${data.phone}"` : "Confirming...";
      default: return "";
    }
  };

  // Auto-animate cursor through fields when modal opens: move → click → write
  useEffect(() => {
    if (open && !isEditing) {
      setShowCursor(true);
      
      const animateField = async (field: 'name' | 'email' | 'phone') => {
        // 1. Move to field
        setCursorField(field);
        setIsClicking(false);
        setTextToType("");
        await new Promise(r => setTimeout(r, 600)); // Wait for cursor to arrive
        
        // 2. Click with ripple
        setIsClicking(true);
        await new Promise(r => setTimeout(r, 500)); // Click animation duration
        
        // 3. Start typing
        setTextToType(getFieldText(field));
        await new Promise(r => setTimeout(r, 1500)); // Time to show typed text
      };

      const sequence = async () => {
        await new Promise(r => setTimeout(r, 500)); // Initial delay
        await animateField('name');
        await animateField('email');
        await animateField('phone');
        
        // Reset
        setCursorField(null);
        setIsClicking(false);
        setTextToType("");
      };
      
      sequence();
    } else {
      setShowCursor(false);
      setCursorField(null);
      setIsClicking(false);
      setTextToType("");
    }
  }, [open, isEditing, data.username, data.email, data.phone]);

  // Force re-render when data changes by using a key based on data values
  const dataKey = `${data.email || ''}-${data.phone || ''}-${data.username || ''}`;
  
  const handleEndCall = () => {
    if (onEndCall) {
      onEndCall();
      onOpenChange(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await onConfirm();
      // Show success state
      setSendSuccess(true);
      // End call gracefully after showing success message (onEndCall handles navigation)
      setTimeout(() => {
        onOpenChange(false);
        // onEndCall already handles navigation to home
        if (onEndCall) {
          onEndCall();
        }
      }, 2500);
    } catch (error) {
      console.error("Error in confirmation:", error);
    }
  };

  const handleStartEdit = (field: 'name' | 'email' | 'phone' | 'all') => {
    setEditField(field);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditField(null);
    setIsEditing(false);
    // Reset to original values
    setEditValues({
      username: data.username || '',
      email: data.email || '',
      phone: data.phone || '',
    });
  };

  const handleSaveEdit = () => {
    if (onDataChange) {
      onDataChange({
        username: editValues.username || undefined,
        email: editValues.email || undefined,
        phone: editValues.phone || undefined,
      });
    }
    setEditField(null);
    setIsEditing(false);
  };

  const renderEditableField = (
    field: 'name' | 'email' | 'phone',
    icon: React.ReactNode,
    label: string,
    value: string | undefined,
    editValue: string,
    delay: number
  ) => {
    const isFieldEditing = isEditing && (editField === field || editField === 'all');
    const fieldKey = field === 'name' ? 'username' : field;

    return (
      <motion.div
        key={`${field}-${value || 'empty'}`}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay }}
        className="relative p-4 rounded-lg bg-white/5 backdrop-blur-sm border border-emerald-500/10 group"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-400/20">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white/60 uppercase tracking-wide mb-1">{label}</p>
            <AnimatePresence mode="wait">
              {isFieldEditing ? (
                <motion.div
                  key="edit"
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                  transition={{ duration: 0.15 }}
                >
                  <Input
                    type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                    value={editValue}
                    onChange={(e) => setEditValues(prev => ({ ...prev, [fieldKey]: e.target.value }))}
                    placeholder={`Enter your ${label.toLowerCase()}`}
                    className="bg-white/10 border-emerald-500/30 text-white placeholder:text-white/40 focus:border-emerald-400 focus:ring-emerald-400/20 h-9"
                    autoFocus={editField === field}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="display"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className={`${field === 'email' ? 'break-all' : ''}`}
                >
                  {value ? (
                    <TypedText text={value} delay={delay * 1000} />
                  ) : (
                    <span className="text-white/40 italic text-base">Not provided</span>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {/* Edit button for individual field */}
          {!isEditing && (
            <button
              onClick={() => handleStartEdit(field)}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-white/10 text-white/50 hover:text-white"
              title={`Edit ${label.toLowerCase()}`}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-[500px] bg-black/90 backdrop-blur-xl border border-emerald-500/20 shadow-2xl relative overflow-visible fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* VAPI Agent Cursor - Move → Click → Write animation */}
        <VapiAgentCursor
          isActive={showCursor && !isEditing && !sendSuccess}
          targetField={cursorField}
          isClicking={isClicking}
          textToType={textToType}
          name="AYRA"
        />
        
        <AnimatePresence mode="wait">
          {sendSuccess ? (
            /* Success State - Clean thank you message */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="py-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 flex items-center justify-center border border-emerald-400/40 mb-6"
              >
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.3 }}
                >
                  <CheckCircle2 className="h-10 w-10 text-emerald-400" />
                </motion.div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="h-5 w-5 text-emerald-400" />
                  <h2 className="text-2xl font-semibold text-white">Thank You!</h2>
                  <Sparkles className="h-5 w-5 text-emerald-400" />
                </div>
                <p className="text-white/70 text-sm mb-4">
                  Your information has been sent successfully.
                </p>
                <p className="text-white/50 text-xs">
                  Redirecting you to home...
                </p>
              </motion.div>
              
              {/* Loading bar animation */}
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 2.5, ease: "linear" }}
                className="h-1 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full mt-6 mx-auto max-w-[200px]"
              />
            </motion.div>
          ) : (
            /* Normal Content */
            <motion.div key="form" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <DialogHeader className="space-y-3">
                <div className="flex items-center justify-center mb-2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center border border-emerald-400/30"
                  >
                    <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                  </motion.div>
                </div>
                <DialogTitle className="text-2xl font-semibold text-white text-center">
                  Confirm Your Information
                </DialogTitle>
                <DialogDescription className="text-center text-white/70 text-sm">
                  Please review the information we collected during our conversation. 
                  Click confirm to send this information to our team.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Data Display Cards with Edit Capability */}
                {renderEditableField(
                  'name',
                  <User className="h-5 w-5 text-emerald-400" />,
                  'Name',
                  data.username,
                  editValues.username,
                  0.1
                )}

                {renderEditableField(
                  'email',
                  <Mail className="h-5 w-5 text-emerald-400" />,
                  'Email',
                  data.email,
                  editValues.email,
                  0.2
                )}

                {renderEditableField(
                  'phone',
                  <Phone className="h-5 w-5 text-emerald-400" />,
                  'Phone',
                  data.phone,
                  editValues.phone,
                  0.3
                )}
              </div>

              {/* Error Message */}
              {sendError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                >
                  {sendError}
                </motion.div>
              )}

              {/* Action Buttons */}
              <div className="pt-4 border-t border-white/10 space-y-3">
                {/* Edit Mode Buttons */}
                <AnimatePresence mode="wait">
                  {isEditing ? (
                    <motion.div
                      key="edit-buttons"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex gap-2"
                    >
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        className="flex-1 bg-white/5 border-white/20 text-white hover:bg-white/10"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveEdit}
                        className="flex-1 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:from-emerald-600 hover:to-cyan-600"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="action-buttons"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-3"
                    >
                      {/* Edit All Button */}
                      <Button
                        onClick={() => handleStartEdit('all')}
                        variant="outline"
                        className="w-full bg-white/5 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-400/50"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit Information
                      </Button>

                      {/* Confirm & Send Button */}
                      <Button
                        onClick={handleConfirm}
                        disabled={isSending}
                        className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:from-emerald-600 hover:to-cyan-600 border-0 shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                      >
                        {isSending ? (
                          <>
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              className="h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"
                            />
                            Sending...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Confirm & Send
                          </>
                        )}
                      </Button>
                      
                      {/* End Call Button */}
                      {onEndCall && (
                        <Button
                          onClick={handleEndCall}
                          disabled={isSending}
                          variant="outline"
                          className="w-full bg-white/5 border-white/20 text-white hover:bg-white/10 hover:border-white/30 disabled:opacity-50"
                        >
                          <PhoneOff className="h-4 w-4 mr-2" />
                          End Call
                        </Button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
